// Plan 08-05 — Web Push registration with module-level idempotency.
//
// NY-08-07: registerWebPush() called twice produces exactly ONE POST to
// /api/push-token. Two layered defenses:
//   1. Module-level promise cache (`_registrationPromise`) — short-circuits
//      the second invocation while the first is still in flight.
//   2. `pushManager.getSubscription()` — if a prior session already
//      subscribed (page reload, returning user), we return the existing
//      subscription WITHOUT subscribing again or POSTing.
//
// Backend de-dups via KV by endpoint hash anyway (Phase 7 Plan 07-05), but
// the client should not even fire — wasted round-trip + log noise.
//
// Per-browser key conversion: PushSubscription.getKey() returns ArrayBuffer
// in the raw byte form; backend expects base64url-encoded strings (per
// /api/push-token openapi schema). We encode without padding.

import { apiFetch } from '../net/client'

let _registrationPromise: Promise<PushSubscription | null> | null = null

/** Test-only: reset the module-level cache between tests. */
export function __resetWebPushModuleCache(): void {
  _registrationPromise = null
}

/**
 * Convert an ArrayBuffer of raw bytes to base64url (no padding).
 * Used for the p256dh / auth keys in the /api/push-token POST body.
 */
function bufferToBase64Url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let bin = ''
  for (let i = 0; i < bytes.length; i += 1) {
    bin += String.fromCharCode(bytes[i] ?? 0)
  }
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * Decode a base64url VAPID public key into the Uint8Array shape
 * `pushManager.subscribe({ applicationServerKey })` expects.
 */
function urlBase64ToUint8Array(b64url: string): Uint8Array {
  const padding = '='.repeat((4 - (b64url.length % 4)) % 4)
  const base64 = (b64url + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i)
  return out
}

interface ServiceWorkerHostNavigator {
  serviceWorker?: {
    register: (
      url: string,
      opts?: { scope?: string },
    ) => Promise<{
      pushManager: {
        getSubscription: () => Promise<PushSubscription | null>
        subscribe: (
          opts: PushSubscriptionOptionsInit,
        ) => Promise<PushSubscription>
      }
    }>
    getRegistration: (scope?: string) => Promise<
      | {
          pushManager: {
            getSubscription: () => Promise<PushSubscription | null>
          }
        }
      | null
      | undefined
    >
  }
}

function browserSupportsWebPush(): boolean {
  // serviceWorker may be undefined either via missing property or `undefined`
  // descriptor (test harness uses the latter); both are unsupported.
  const nav = globalThis.navigator as ServiceWorkerHostNavigator | undefined
  if (!nav || !nav.serviceWorker) return false
  if (
    typeof (globalThis as { PushManager?: unknown }).PushManager === 'undefined'
  )
    return false
  return true
}

/**
 * Options for registerWebPush.
 *
 * `skipPermissionPrompt` (WR-04 fix): when the caller has ALREADY invoked
 * Notification.requestPermission() in the same user-gesture frame and
 * verified the result is 'granted', they may pass `true` so registerWebPush
 * does NOT re-prompt. This avoids a second requestPermission() landing past
 * an `await` boundary (Safari iOS user-gesture rule — Pitfall 1 in
 * 16-RESEARCH.md). The second call is normally a no-op (returns the
 * cached 'granted' state) but a future Safari version that re-prompts on
 * cached-granted would fail silently.
 *
 * If `skipPermissionPrompt` is false/undefined, registerWebPush calls
 * Notification.requestPermission() itself — preserves the prior call-shape
 * for direct invocations from settings flows that did not pre-prompt.
 */
export interface RegisterWebPushOptions {
  skipPermissionPrompt?: boolean
}

/**
 * Register the Service Worker, request notification permission, subscribe
 * to push, and POST the subscription to /api/push-token.
 *
 * Returns the PushSubscription (existing or newly-created) on success;
 * `null` on permission denied OR browser without SW/PushManager support.
 *
 * Idempotent — a second call within the same browser session NEVER triggers
 * a second `/api/push-token` POST.
 */
export function registerWebPush(
  opts?: RegisterWebPushOptions,
): Promise<PushSubscription | null> {
  if (_registrationPromise !== null) return _registrationPromise
  _registrationPromise = (async () => {
    if (!browserSupportsWebPush()) return null
    const nav = globalThis.navigator as ServiceWorkerHostNavigator
    const sw = nav.serviceWorker
    if (!sw) return null

    const reg = await sw.register('/sw.js', { scope: '/' })

    // Step 1 — short-circuit if a previous session already subscribed.
    const existing = await reg.pushManager.getSubscription()
    if (existing) {
      // Already subscribed; do NOT re-POST (NY-08-07 strict-no-double-POST).
      return existing
    }

    // Step 2 — request permission (unless the caller pre-verified inside
    // the same user-gesture frame and asked us to skip — WR-04 fix).
    if (!opts?.skipPermissionPrompt) {
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') return null
    }

    // Step 3 — subscribe.
    const vapid = import.meta.env?.VITE_VAPID_PUBLIC_KEY
    if (!vapid || vapid.startsWith('<')) {
      // Placeholder still in place — operator setup gap. Bail gracefully.
      return null
    }
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(
        vapid,
      ) as unknown as Uint8Array<ArrayBuffer>,
    })

    // Step 4 — POST to backend.
    const p256dhBuf = sub.getKey('p256dh')
    const authBuf = sub.getKey('auth')
    if (!p256dhBuf || !authBuf) {
      // Browser refused to expose the keys; cannot form a valid Web Push
      // subscription payload. Surface as null so caller can re-try later.
      return null
    }
    await apiFetch('/api/push-token', 'POST', {
      body: {
        platform: 'webpush',
        endpoint: sub.endpoint,
        keys: {
          p256dh: bufferToBase64Url(p256dhBuf),
          auth: bufferToBase64Url(authBuf),
        },
      },
    })
    return sub
  })()
  return _registrationPromise
}

/**
 * Inverse of registerWebPush — unsubscribe locally + tell the backend to
 * forget the token. Idempotent (no-op if no subscription).
 */
export async function unregisterWebPush(): Promise<void> {
  _registrationPromise = null
  if (!browserSupportsWebPush()) return
  const nav = globalThis.navigator as ServiceWorkerHostNavigator
  const sw = nav.serviceWorker
  if (!sw) return
  const reg = await sw.getRegistration('/')
  if (!reg) return
  const sub = await reg.pushManager.getSubscription()
  if (!sub) return
  await sub.unsubscribe()
  await apiFetch('/api/push-token', 'DELETE', {})
}
